import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "w-100" }
const _hoisted_2 = ["for"]
const _hoisted_3 = ["id", "step", "min", "max", "disabled", "value"]
const _hoisted_4 = {
  key: 1,
  class: "invalid-feedback d-block"
}
const _hoisted_5 = {
  key: 2,
  class: "text-muted d-block mt-1"
}

import { ref, computed, watch } from 'vue'

const CO_OFFSET = '-05:00'

export default /*#__PURE__*/_defineComponent({
  __name: 'AppDateTimeInput',
  props: {
    modelValue: {},
    label: {},
    id: {},
    required: { type: Boolean },
    wasValidated: { type: Boolean },
    invalidMessage: {},
    disabled: { type: Boolean },
    min: {},
    max: {},
    step: {},
    mode: {},
    debug: { type: Boolean }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit }) {

const props = __props;





const id = computed(() => props.id || `dt-${Math.random().toString(36).slice(2)}`)
const touched = ref(false)

const effectiveStep = computed(() => (props.step && props.step > 0 ? props.step : 60))
const withSeconds = computed(() => effectiveStep.value < 60 || (effectiveStep.value % 60) !== 0)

const showInvalid = computed(() =>
  !!props.required && (props.wasValidated || touched.value) && !props.modelValue
)
const invalidText = computed(() => props.invalidMessage || 'Campo requerido')

function pad(n: number) { return String(n).padStart(2, '0') }

/** strict local 'YYYY-MM-DDTHH:mm[:ss]' → Date (local) */
function fromLocalPattern(s: string): Date | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (!m) return null
  const [ , Y, M, D, h, mi, se ] = m
  const d = new Date(
    Number(Y),
    Number(M) - 1,
    Number(D),
    Number(h),
    Number(mi),
    se ? Number(se) : 0,
    0
  )
  return isNaN(d.getTime()) ? null : d
}

/** Date → 'YYYY-MM-DDTHH:mm[:ss]' for the input */
function toInputLocalString(d: Date, includeSeconds: boolean): string {
  if (isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hh = pad(d.getHours())
  const mm = pad(d.getMinutes())
  const ss = pad(d.getSeconds())
  return includeSeconds
    ? `${y}-${m}-${day}T${hh}:${mm}:${ss}`
    : `${y}-${m}-${day}T${hh}:${mm}`
}

/** Any incoming string (ISO Z, ISO offset, or local) → local input string */
function toLocalInputStr(src: string | null | undefined, includeSeconds: boolean): string {
  if (!src) return ''
  // local fast path
  const local = fromLocalPattern(src)
  if (local) return toInputLocalString(local, includeSeconds)
  // ISO parse
  const d = new Date(src)
  if (isNaN(d.getTime())) return ''
  return toInputLocalString(d, includeSeconds)
}

/** Emit according to mode */
function buildOutput(localStr: string): string | null {
  if (!localStr) return null
  const d = fromLocalPattern(localStr)
  if (!d) return null

  const mode = props.mode || 'offset'
  if (mode === 'local') return localStr
  if (mode === 'utc') return new Date(d.getTime()).toISOString()

  // 'offset' (Colombia)
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hh = pad(d.getHours())
  const mm = pad(d.getMinutes())
  const ss = withSeconds.value ? `:${pad(d.getSeconds())}` : ':00'
  return `${y}-${m}-${day}T${hh}:${mm}${ss}${CO_OFFSET}`
}

/** Convert min/max to local input format */
function toLocalConstraint(v?: string | null): string | undefined {
  if (!v) return undefined
  const s = toLocalInputStr(v, withSeconds.value)
  return s || undefined
}

const minLocal = computed(() => toLocalConstraint(props.min ?? undefined))
const maxLocal = computed(() => toLocalConstraint(props.max ?? undefined))

// bound value for the <input>
const localStr = ref<string>('')

// keep input synced with external model
watch(
  () => props.modelValue,
  (val) => {
    localStr.value = toLocalInputStr(val, withSeconds.value)
  },
  { immediate: true }
)

// events
function onInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  localStr.value = val
  emit('update:modelValue', buildOutput(val))
}
function onChange() {
  emit('change', buildOutput(localStr.value))
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "form-label fw-medium text-black mb-1",
          for: id.value
        }, _toDisplayString(_ctx.label), 9, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("input", {
      id: id.value,
      class: "form-control form-control-sm",
      type: "datetime-local",
      step: effectiveStep.value,
      min: minLocal.value,
      max: maxLocal.value,
      disabled: _ctx.disabled,
      value: localStr.value,
      onInput: onInput,
      onChange: onChange,
      onBlur: _cache[0] || (_cache[0] = ($event: any) => (touched.value = true)),
      "aria-label": "Date and time input"
    }, null, 40, _hoisted_3),
    (showInvalid.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(invalidText.value), 1))
      : _createCommentVNode("", true),
    (_ctx.debug)
      ? (_openBlock(), _createElementBlock("small", _hoisted_5, " modelValue → " + _toDisplayString(_ctx.modelValue), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})